import React, { useState, useEffect } from 'react';
import axios from 'axios';
import apiClient from '../services/webTicketApiClient';
import trustApiClient from '../services/apiClient';
import { useParams } from 'react-router-dom';
import './Ticket.css'; // Import the stylesheet

const getStoredUserEmail = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}').email || '';
  } catch {
    return '';
  }
};

const getCurrentUserMobile = async () => {
  let mobile = (localStorage.getItem('userMobile') || '').replace(/\D/g, '');
  if (/^\d{10}$/.test(mobile)) return mobile;

  const email = getStoredUserEmail();
  if (!email) return '';

  const response = await trustApiClient.post('/api/v2/contacts/get-mobile-by-email', { email });
  mobile = String(response.data || '').replace(/\D/g, '');

  if (/^\d{10}$/.test(mobile)) {
    localStorage.setItem('userMobile', mobile);
    return mobile;
  }

  return '';
};

const getTickets = (responseData) => {
  if (Array.isArray(responseData)) return responseData;

  const collections = [
    responseData?.tickets,
    responseData?.data,
    responseData?.content,
    responseData?.data?.tickets,
    responseData?.data?.content,
  ];

  return collections.find(Array.isArray);
};

// Helper function to format the date and time
const formatDateTime = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  const options = {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true
  };
  return date.toLocaleString('en-US', options);
};

// Helper to check for valid string content from the API
const isValidString = (str) => {
  return str && str !== 'string';
};

function JobManagement({ isSessionReady, onTicketSelect, selectedJobRunId }) {
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { jobRunId } = useParams();

  useEffect(() => {
    if (!isSessionReady) {
      return;
    }

    const controller = new AbortController();

    const fetchJobs = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const createdBy = await getCurrentUserMobile();
        const response = await apiClient.get("/api/v2/web/tickets", {
          signal: controller.signal,
          params: createdBy ? { createdBy } : undefined,
        });
        let tickets = getTickets(response.data);

        if (!tickets) {
          throw new Error('The ticket service returned an unexpected response.');
        }

        if (tickets.length === 0 && createdBy) {
          const legacyResponse = await trustApiClient.get('/api/v2/jobs', {
            signal: controller.signal,
            params: { createdBy },
          });
          tickets = getTickets(legacyResponse.data);

          if (!tickets) {
            throw new Error('The ticket service returned an unexpected response.');
          }
        }

        setJobs(tickets);
      } catch (err) {
        if (axios.isCancel(err)) {
          console.log('Request canceled', err.message);
        } else {
          setError(
            err.response?.data?.message
            || err.response?.data?.msg
            || err.message
            || 'Failed to fetch the list of tickets for this user.'
          );
          console.error(err);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchJobs();

    return () => {
      controller.abort();
    };
  }, [isSessionReady]);

  return (
    <div className="section ticket-list">
      <h2>All Tickets</h2>
      {!isSessionReady && <div>Loading tickets...</div>}
      {isSessionReady && isLoading && <div>Loading tickets...</div>}
      {error && <div style={{ color: 'red' }}>{error}</div>}
      {!isLoading && !error && jobs.length === 0 && <div>No tickets found for this user.</div>}
      {!isLoading && !error && jobs.length > 0 && (
        <>
          {!jobRunId && !selectedJobRunId && (
            <p className="ticket-selection-instruction">
              Please select a ticket from the list to see the details.
            </p>
          )}
          <div className="ticket-table-wrapper">
            <table className="ticket-table">
              <thead>
                <tr>
                  <th scope="col">Ticket</th>
                  <th scope="col">Created</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map(job => (
                  <tr key={job.jobRunId} className={job.jobRunId === (selectedJobRunId || jobRunId) ? 'selected' : ''}>
                    <td>
                      <button
                        type="button"
                        className="ticket-link"
                        onClick={() => onTicketSelect?.(job.jobRunId)}
                      >
                        <strong>#{job.jobRunId.substring(4, 10)}</strong>: {isValidString(job.jobTitle) ? job.jobTitle : job.jobName}
                        {isValidString(job.jobDescription) && (
                          <div className="ticket-description">
                            {job.jobDescription}
                          </div>
                        )}
                      </button>
                    </td>
                    <td>{formatDateTime(job.startTime)}</td>
                    <td>
                      <span className={`badge status-${job.status.toLowerCase()}`}>
                        {job.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default JobManagement;
